import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { SCHEMES, getScheme } from "@/data/schemes";

const SITE = "https://hireds.in";

export default function SchemeDetail() {
  const { slug } = useParams();
  const scheme = getScheme(slug);

  useEffect(() => {
    if (!scheme) return;
    const oldTitle = document.title;
    document.title = `${scheme.name} 2026 — Eligibility, Benefit Amount & How to Apply | hireds.in`;
    const md = document.querySelector('meta[name="description"]');
    const oldMd = md ? md.getAttribute("content") : null;
    if (md) md.setAttribute("content", `Complete ${scheme.name} guide — eligibility, exact benefit amount, step-by-step apply process and official portal link. Updated 2026.`);
    const canon = document.querySelector('link[rel="canonical"]');
    const oldCanon = canon ? canon.getAttribute("href") : null;
    if (canon) canon.setAttribute("href", `${SITE}/schemes/${scheme.slug}`);
    return () => {
      document.title = oldTitle;
      if (md && oldMd != null) md.setAttribute("content", oldMd);
      if (canon && oldCanon != null) canon.setAttribute("href", oldCanon);
    };
  }, [scheme]);

  if (!scheme) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 text-center" data-testid="scheme-not-found">
        <h1 className="text-2xl font-extrabold text-slate-900">Scheme not found</h1>
        <p className="text-sm text-slate-600 mt-2">
          The scheme you tried to open is not in our database yet. Browse <Link to="/schemes" className="text-orange-600 underline">all government schemes</Link>.
        </p>
      </div>
    );
  }

  const related = SCHEMES.filter((s) => s.slug !== scheme.slug).slice(0, 4);

  return (
    <article className="max-w-3xl mx-auto px-4 md:px-6 py-6" data-testid="scheme-detail">
      <Breadcrumbs items={[
        { name: "Home", url: "/" },
        { name: "Schemes", url: "/schemes" },
        { name: scheme.name, url: `/schemes/${scheme.slug}` },
      ]} />
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600">{scheme.ministry}</p>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          {scheme.name}
        </h1>
        <p className="text-sm text-slate-500 mt-2">Launched: {scheme.launched}</p>
      </header>

      <section className="space-y-6 text-slate-800 leading-relaxed">
        <div>
          <h2 className="text-xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>Who is this scheme for?</h2>
          <p className="text-base">{scheme.target}</p>
        </div>

        <div className="rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-5">
          <h2 className="text-xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>Benefit amount &amp; what you get</h2>
          <p className="text-base">{scheme.benefit}</p>
        </div>

        <div>
          <h2 className="text-xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>Eligibility criteria</h2>
          <p className="text-base">{scheme.eligibility}</p>
        </div>

        <div>
          <h2 className="text-xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>How to apply — step by step</h2>
          <p className="text-base">{scheme.how_to_apply}</p>
        </div>

        <div className="flex flex-wrap gap-3 py-3">
          <a
            href={scheme.portal}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="scheme-portal-link"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 transition"
          >
            Visit Official Portal <ExternalLink className="w-4 h-4" aria-hidden="true" />
          </a>
          <a
            href={scheme.pib_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-slate-300 bg-white text-slate-900 text-sm font-semibold hover:border-slate-400 transition"
          >
            Read PIB / Official Notification <ExternalLink className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
      </section>

      <section className="mt-10 border-t border-slate-200 pt-6">
        <h2 className="text-xl font-extrabold text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>Related schemes</h2>
        <ul className="grid sm:grid-cols-2 gap-3">
          {related.map((r) => (
            <li key={r.slug}>
              <Link to={`/schemes/${r.slug}`} className="block p-3 rounded-md border border-slate-200 bg-white hover:border-orange-400 text-sm">
                <strong className="text-slate-900">{r.name}</strong>
                <span className="block text-xs text-slate-500 mt-0.5">{r.ministry}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <p className="mt-8 text-xs text-slate-500 leading-relaxed">
        Disclaimer: hireds.in is an independent editorial portal. We summarise official scheme notifications for ease of reading. Always verify the latest benefit amount and eligibility on the official portal linked above before applying.
      </p>
    </article>
  );
}
